import { useMutation, useQueryClient } from '@tanstack/react-query';
import { withholdingsQueryKey } from './keys';
import { normalizeWithholding, sortWithholdings } from '../types';
import type {
  ModifyWithholdingPayload,
  WithholdingChildEnvelope,
  WithholdingDto,
  WithholdingsHttpRequest,
} from '../types';

export interface UseModifyWithholdingParams {
  documentId: string;
  /** Endpoint base de child (`/{id}/withholdings`); se le añade `/{childId}`. */
  withholdingsBaseEndpoint: string;
  httpRequest: WithholdingsHttpRequest;
}

export interface ModifyWithholdingInput {
  withholdingId: string;
  payload: ModifyWithholdingPayload;
}

interface ModifyMutationContext {
  previous?: WithholdingDto[];
}

/**
 * Modifica una retención (`PUT /{id}/withholdings/{childId}`) → 200 `{child, header}`,
 * con optimistic update.
 *
 * - `onMutate`: aplica base/total/acción a la fila. Si era automática (no manual),
 *   la marca `isManipulated = true` (espejo del comportamiento del backend).
 * - `onError`: rollback al snapshot previo (el panel surfacea 409/422/403).
 * - `onSuccess`: reemplaza la fila con la respuesta del servidor (rowVersion++).
 * - `onSettled`: invalida `['document-withholdings', documentId]`.
 */
export function useModifyWithholding({
  documentId,
  withholdingsBaseEndpoint,
  httpRequest,
}: UseModifyWithholdingParams) {
  const queryClient = useQueryClient();
  const key = withholdingsQueryKey(documentId);

  return useMutation<WithholdingDto, Error, ModifyWithholdingInput, ModifyMutationContext>({
    mutationFn: async ({ withholdingId, payload }) => {
      const res = await httpRequest<WithholdingChildEnvelope>(
        'PUT',
        `${withholdingsBaseEndpoint}/${withholdingId}`,
        payload,
      );
      return normalizeWithholding(res.child);
    },
    onMutate: async ({ withholdingId, payload }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<WithholdingDto[]>(key);

      queryClient.setQueryData<WithholdingDto[]>(key, (prev) =>
        (prev ?? []).map((row) =>
          row.id === withholdingId
            ? {
                ...row,
                baseValue: payload.baseValue,
                totalValue: payload.totalValue,
                withholdingAction: payload.withholdingAction,
                // Editar una fila automática la convierte en manipulada.
                isManipulated: row.isManual ? row.isManipulated : true,
              }
            : row,
        ),
      );

      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(key, context.previous);
      }
    },
    onSuccess: (updated) => {
      queryClient.setQueryData<WithholdingDto[]>(key, (prev) =>
        sortWithholdings((prev ?? []).map((row) => (row.id === updated.id ? updated : row))),
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
}
